/**
 * RouteLoader — branded fallback rendered by the App-root Suspense
 * boundary while a lazy-loaded route chunk downloads. Replaces the
 * old `fallback={null}` which flashed a blank black screen between
 * navigation and the new route's first paint.
 *
 * UX intent: keep it under 200ms-perceived. Three signals all happen
 * simultaneously:
 *   1. Soft pulse on the BC monogram so the visitor knows something
 *      is happening (not a broken page).
 *   2. Emerald-tinted glow accent matching the chat-widget online dot
 *      and the AboutPage hero halo — ties the loader into the visual
 *      vocabulary the rest of the site uses.
 *   3. Aria-busy + aria-label so screen readers announce the wait
 *      instead of going silent.
 *
 * Z-index is set just below the chat widget (z-150) so if the visitor
 * navigates while the widget is open, the widget stays on top.
 */

import { useEffect, useState } from 'react';

interface RouteLoaderProps {
  /**
   * Delay (ms) before the loader becomes visible. Hides the loader
   * entirely for fast chunk loads — flashing the loader for 50ms
   * looks worse than no loader at all. Default 180ms — below the
   * "this page is broken" threshold but enough to skip cached chunk
   * loads.
   */
  delayMs?: number;
}

export const RouteLoader = ({ delayMs = 180 }: RouteLoaderProps) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(() => setVisible(true), delayMs);
    return () => window.clearTimeout(t);
  }, [delayMs]);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[140] flex items-center justify-center bg-black/85 backdrop-blur-sm pointer-events-none"
      role="status"
      aria-busy="true"
      aria-label="Loading page"
    >
      <div className="relative">
        {/* Soft emerald halo — matches the chat-widget online dot +
            AboutPage hero halo so the brand vocabulary is consistent. */}
        <div className="absolute -inset-4 rounded-full bg-emerald-400/15 blur-2xl pointer-events-none animate-pulse" />
        {/* BC keycap monogram — the on-dark variant used in the footer, so
            it reads correctly against the loader's dark scrim. Soft pulse so
            it reads as "active wait" not "frozen". */}
        <span className="relative inline-flex items-center justify-center animate-pulse" aria-hidden="true">
          <svg viewBox="0 0 64 64" width="72" height="72" fill="none">
            <rect x="8" y="9" width="48" height="48" rx="14" fill="#2c2c31" stroke="#f4f4f5" strokeWidth="3" />
            <g>
              <rect x="8" y="9" width="48" height="42" rx="14" fill="#17181b" stroke="#f4f4f5" strokeWidth="3" />
              <rect x="14" y="15" width="36" height="30" rx="8" fill="none" stroke="#f4f4f5" strokeWidth="1.4" opacity="0.3" />
              <text x="32" y="30.5" fontFamily='"Hanken Grotesk", system-ui, sans-serif' fontSize="19" fontWeight="700" fill="#f4f4f5" textAnchor="middle" dominantBaseline="central" letterSpacing="-1.2">BC</text>
            </g>
          </svg>
        </span>
      </div>
      <span className="sr-only">Loading…</span>
    </div>
  );
};
